import BigNumber from 'bignumber.js'
import {
  compareAsc,
  format,
  isBefore,
  parse,
  startOfDay,
  startOfMonth,
} from 'date-fns'

import type { ChartEntry } from './pnl-chart-entry'

export type PnlCalendarEntry = Omit<ChartEntry, 'pnl'>

export type DayKey = string

export const toDayKey = (date: Date): DayKey => format(date, 'yyyy-MM-dd')

/**
 * Daily realized PnL from cumulative snapshots: last cumulative value on a calendar
 * day minus the last cumulative strictly before that day starts.
 */
export const buildDailyPnlByDayKey = (
  entries: ReadonlyArray<PnlCalendarEntry>,
): Map<DayKey, { pnlNative: BigNumber; pnlUsd: BigNumber }> => {
  if (entries.length === 0) {
    return new Map<DayKey, { pnlNative: BigNumber; pnlUsd: BigNumber }>()
  }

  const sorted = [...entries].sort((a, b) =>
    compareAsc(a.timestamp, b.timestamp),
  )

  const dayToClose = new Map<
    DayKey,
    { pnlNative: BigNumber; pnlUsd: BigNumber }
  >()
  for (const e of sorted) {
    dayToClose.set(toDayKey(startOfDay(e.timestamp)), {
      pnlNative: e.pnlNative,
      pnlUsd: e.pnlUsd,
    })
  }

  const days = [...dayToClose.keys()].sort()
  const result = new Map<DayKey, { pnlNative: BigNumber; pnlUsd: BigNumber }>()

  for (const dayKey of days) {
    const close = dayToClose.get(dayKey)!
    const dayStart = startOfDay(parse(dayKey, 'yyyy-MM-dd', new Date()))

    let prevClose = { pnlNative: new BigNumber(0), pnlUsd: new BigNumber(0) }
    for (let j = sorted.length - 1; j >= 0; j--) {
      const e = sorted[j]!
      if (isBefore(e.timestamp, dayStart)) {
        prevClose = {
          pnlNative: e.pnlNative,
          pnlUsd: e.pnlUsd,
        }
        break
      }
    }

    result.set(dayKey, {
      pnlNative: close.pnlNative.minus(prevClose.pnlNative),
      pnlUsd: close.pnlUsd.minus(prevClose.pnlUsd),
    })
  }

  return result
}

export const earliestMonthStart = (
  entries: ReadonlyArray<PnlCalendarEntry>,
): Date => {
  if (entries.length === 0) {
    return startOfMonth(new Date())
  }

  let min = entries[0]!.timestamp
  for (const e of entries) {
    if (e.timestamp < min) {
      min = e.timestamp
    }
  }

  return startOfMonth(min)
}
